// Database-backed SavedItemsService
// Replaces localStorage with API calls to the saves backend

import { API_BASE } from '../config/api';
import AuthService from './authService';
import generateId from '../utils/generateId';

class SavedItemsServiceDB {
  static API_URL = `${API_BASE}/api`;

  // Use centralized AuthService.handleResponse for graceful auth error handling

  // Helper to determine activity category from types
  static getActivityCategory(types) {
    if (!types || types.length === 0) return 'other';

    const typeString = types.join(' ').toLowerCase();

    if (typeString.includes('restaurant') || typeString.includes('food') ||
        typeString.includes('cafe') || typeString.includes('bakery')) {
      return 'food';
    }
    if (typeString.includes('museum') || typeString.includes('art') ||
        typeString.includes('cultural') || typeString.includes('historic')) {
      return 'culture';
    }
    if (typeString.includes('park') || typeString.includes('outdoor') ||
        typeString.includes('nature') || typeString.includes('garden')) {
      return 'parks';
    }
    if (typeString.includes('entertainment') || typeString.includes('cinema') ||
        typeString.includes('theater') || typeString.includes('concert')) {
      return 'entertainment';
    }
    if (typeString.includes('shopping') || typeString.includes('store') ||
        typeString.includes('mall')) {
      return 'shopping';
    }
    if (typeString.includes('education') || typeString.includes('library') ||
        typeString.includes('university')) {
      return 'education';
    }

    return 'other';
  }

  // === SAVE METHODS ===

  // Save an activity to the database.
  // options.foundVia: the post this was saved from, { type, id } — credits it
  // (docs/plans/POST_SAVE_ATTRIBUTION.md); the server validates it.
  // A save names no place (C367): it sends no location_confirmed or
  // confirmed_types, and the server ignores both from an old client.
  static async saveActivity(activity, fromTrip = null, tags = [], collectionId = null, { foundVia = null } = {}) {
    try {
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/saves`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          content_id: activity.place_id || activity.activity_id || activity.id || generateId('act'),
          tags: activity.types || activity.tags || tags,
          user_notes: activity.notes || '',
          found_via_type: foundVia?.type || null,
          found_via_id: foundVia?.id || null,
          // Store full activity data for reference
          metadata: {
            title: activity.name || activity.title,
            description: activity.description || activity.editorial_summary?.overview,
            location: activity.location || activity.vicinity,
            // Same shape cascade as VisitConfirmationModal's placeLat/placeLng.
            // (savesRoutes no longer writes it onto user_place_data: a save
            // names no place, C367.)
            coordinates: (() => {
              const lat = activity.lat ?? activity.coordinates?.lat ?? activity.location?.latitude ?? activity.geometry?.location?.lat ?? null;
              const lng = activity.lng ?? activity.coordinates?.lng ?? activity.location?.longitude ?? activity.geometry?.location?.lng ?? null;
              return lat != null && lng != null ? { lat, lng } : null;
            })(),
            rating: activity.rating,
            priceRange: activity.priceRange,
            // No image: the server resolves each saved card's picture when it
            // is drawn, for the person viewing it, and ignores any sent here
            // (docs/TAKE_IT_DOWN.md §3.10).
            fromTrip: fromTrip,
            sourceType: activity.sourceType || (fromTrip ? 'trip' : 'search'),
            // Include all event-specific fields for Ticketmaster events
            eventDate: activity.eventDate,
            eventTime: activity.eventTime,
            venue: activity.venue,
            city: activity.city,
            state: activity.state,
            price: activity.price,
            genre: activity.genre,
            subGenre: activity.subGenre,
            tags: activity.tags,
            segment: activity.segment,
            url: activity.url,
            category: activity.category,
            // Tiqets venue-less products: hoist the experience id so the
            // savesRoutes ensureTiqetsActivity hook reads it from a stable
            // top-level field instead of falling back to content_id.
            tiqetsProductCode: activity.tiqetsProductCode || activity.tiqets_product_code,
            // Include raw event data (strip large photo blobs to avoid payload limits)
            rawEventData: activity.rawEventData || (() => {
              const { primaryUserPhoto, wikimediaPhoto, googleData, image, ...slim } = activity;
              return slim;
            })()
          }
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error saving activity:', error);
      return { success: false, error: error.message };
    }
  }

  // Say where a place being saved somewhere other than My Activities (a trip
  // collection) was found, before it is added. Best-effort: never fails a save.
  static async recordFoundVia(contentId, foundVia = null) {
    try {
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/saves/found-via`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          content_id: contentId,
          found_via_type: foundVia?.type || null,
          found_via_id: foundVia?.id || null
        })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error recording where a save was found:', error);
      return { success: false, error: error.message };
    }
  }

  // A post's "Saved by N" and the viewer's standing in it, read after a save
  // from its card lands (utils/postSaveCount). Null on any failure.
  static async getSaveStanding(type, id) {
    try {
      const response = await fetch(
        `${SavedItemsServiceDB.API_URL}/saves/standing/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
        { headers: AuthService.getJsonHeaders() });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.success ? data : null;
    } catch (error) {
      console.error('Error reading save standing:', error);
      return null;
    }
  }

  // Which of these cards the viewer has saved — the same definition every
  // bookmark reads (server/services/viewerSaved.js). items: [{ id,
  // content_type, place_id? }]. A Set of ids; empty on any failure.
  static async getSavedState(items) {
    try {
      if (!Array.isArray(items) || items.length === 0) return new Set();
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/saves/saved-state`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ items })
      });
      if (!response.ok) return new Set();
      const data = await response.json();
      return new Set(data?.success ? (data.saved_ids || []) : []);
    } catch (error) {
      console.error('Error reading saved state:', error);
      return new Set();
    }
  }

  // Save a trip to collections
  static async saveTrip(trip, tags = []) {
    try {
      // For trips, we'll save to collections API
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/collections`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          name: trip.name || trip.title,
          description: trip.description || `Saved trip: ${trip.name}`,
          color: 'blue',
          metadata: {
            sourceType: 'social',
            fromUser: trip.createdBy || trip.userId,
            originalTripId: trip.id,
            tags: tags,
            tripData: trip
          }
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error saving trip:', error);
      return { success: false, error: error.message };
    }
  }

  // === GET METHODS ===

  /**
   * Get all saved activity IDs for efficient bulk lookups.
   * Returns just content_ids without metadata for checking saved status in search results.
   * @returns {Promise<string[]>} Array of content_id strings
   */
  static async getSavedActivityIds() {
    try {
      const response = await fetch(`${this.API_URL}/saves/ids`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await AuthService.handleResponse(response);
      return data.ids || [];
    } catch (error) {
      console.error('Error loading saved activity IDs:', error);
      return [];
    }
  }

  // Get all saved activities (with optional pagination)
  static async getSavedActivities(userId = null, filters = {}) {
    try {
      let url = `${SavedItemsServiceDB.API_URL}/saves`;
      const params = new URLSearchParams();

      if (filters.collection) {
        params.append('collection', filters.collection);
      }
      if (filters.tags && filters.tags.length > 0) {
        params.append('tags', filters.tags.join(','));
      }
      if (filters.sourceTripId) {
        params.append('source_trip_id', filters.sourceTripId);
      }
      // Pagination params
      if (filters.limit !== undefined) {
        params.append('limit', filters.limit.toString());
      }
      if (filters.offset !== undefined) {
        params.append('offset', filters.offset.toString());
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);

      // Transform saves back to activity format for compatibility
      const transformedSaves = (data.saves || []).map(save => {
        const title = save.title || save.displayName || save.customTitle || save.contentId;
        const description = save.description || '';
        const location = save.placeAddress || save.location || '';
        const imageUrl = save.imageUrl || save.image || null;
        const eventDate = save.eventDate || null;
        const eventTime = save.eventTime || null;
        const price = save.price || null;
        const url = save.url || save.externalUrl || null;
        const category = save.category || this.getActivityCategory(save.tags || []);
        const source = save.sourceType || 'unknown';

        return {
          id: save.id,
          databaseId: save.databaseId,
          // The server resolves place_id through the entity spine (a saved
          // landmark with an approved Google link gets the real ChIJ… id) —
          // don't clobber it back to the content id, or the detail link and
          // place-keyed consumers lose the resolution.
          place_id: save.place_id || save.contentId,
          content_id: save.contentId,
          name: title,
          title: title,
          description: description,
          location: location,
          place_address: save.placeAddress || '',
          venue: save.venue,
          city: save.city,
          state: save.state,
          country: save.country,
          vicinity: location,
          rating: save.rating,
          place_rating: save.rating,
          price: price,
          priceRange: price,
          photoReference: save.photoReference,
          imageUrl: imageUrl,
          // Exact Commons credit (licence condition) — without this the card
          // falls back to the generic host-derived "Photo: Wikimedia Commons".
          photoAttribution: save.photoAttribution || null,
          eventDate: eventDate,
          eventTime: eventTime,
          url: url,
          externalUrl: url,
          source: source,
          category: category,
          genre: save.genre,
          segment: save.segment,
          subGenre: save.subGenre,
          types: save.tags || [],
          tags: save.tags || [],
          notes: save.userNotes,
          savedAt: save.savedAt,
          collection: save.collectionNameDisplay || save.collectionName,
          collection_id: save.collectionId,
          collection_color: save.collectionColor,
          sourceType: source,
          fromTrip: save.fromTrip || null,
          photos: save.photos || [],
          total_photos: save.total_photos || 0,
          hasMorePhotos: (save.total_photos || 0) > (save.photos?.length || 0),

          data: {
            id: save.id,
            databaseId: save.databaseId,
            place_id: save.place_id || save.contentId,
            placeId: save.place_id || save.contentId,
            name: title,
            title: title,
            description: description,
            location: location,
            place_address: save.placeAddress || '',
            venue: save.venue,
            city: save.city,
            state: save.state,
            country: save.country,
            vicinity: location,
            rating: save.rating,
            price: price,
            priceRange: price,
            photoReference: save.photoReference,
            imageUrl: imageUrl,
            image: imageUrl,
            photoAttribution: save.photoAttribution || null,
            eventDate: eventDate,
            eventTime: eventTime,
            url: url,
            externalUrl: url,
            source: source,
            category: category,
            genre: save.genre,
            segment: save.segment,
            subGenre: save.subGenre,
            types: save.tags || [],
            tags: save.tags || [],
            photos: save.photos || [],
            total_photos: save.total_photos || 0,
            hasMorePhotos: (save.total_photos || 0) > (save.photos?.length || 0)
          }
        };
      });

      // If pagination was requested, return object with pagination info
      if (filters.limit !== undefined || filters.offset !== undefined) {
        return {
          activities: transformedSaves,
          pagination: data.pagination || {
            total: transformedSaves.length,
            limit: filters.limit || 20,
            offset: filters.offset || 0,
            hasMore: false
          }
        };
      }

      // Otherwise return just the array for backward compatibility
      return transformedSaves;
    } catch (error) {
      console.error('Error getting saved activities:', error);
      // Return appropriate format based on whether pagination was requested
      if (filters.limit !== undefined || filters.offset !== undefined) {
        return { activities: [], pagination: { total: 0, limit: 20, offset: 0, hasMore: false } };
      }
      return [];
    }
  }

  // Get saved trips (from collections AND travel boards)
  static async getSavedTrips(userId = null, filters = {}) {
    try {
      // Fetch from both sources in parallel
      const [collectionsResponse, boardsResponse] = await Promise.all([
        fetch(`${SavedItemsServiceDB.API_URL}/collections`, { headers: AuthService.getJsonHeaders() }),
        fetch(`${SavedItemsServiceDB.API_URL}/travel-boards`, { headers: AuthService.getJsonHeaders() })
      ]);

      const collectionsData = await AuthService.handleResponse(collectionsResponse);
      const boardsData = await AuthService.handleResponse(boardsResponse);

      // Get trips from collections (legacy system)
      const collectionTrips = (collectionsData.collections || [])
        .filter(collection => collection.metadata?.sourceType === 'social')
        .map(collection => ({
          id: collection.metadata?.originalTripId || collection.id,
          name: collection.name,
          title: collection.name,
          description: collection.description,
          savedAt: collection.created_at,
          sourceType: 'social',
          fromUser: collection.metadata?.fromUser,
          tags: collection.metadata?.tags || [],
          tripData: collection.metadata?.tripData,
          source: 'collection'
        }));

      // Get trips from travel boards
      const boardTrips = [];
      const boards = boardsData.boards || [];

      // Fetch items from each board and collect trips
      for (const board of boards) {
        try {
          const itemsResponse = await fetch(`${SavedItemsServiceDB.API_URL}/travel-boards/${board.id}/items`, {
            headers: AuthService.getJsonHeaders()
          });
          const itemsData = await AuthService.handleResponse(itemsResponse);

          // Filter for trip items and transform them
          const trips = (itemsData.items || [])
            .filter(item => item.content_type === 'trip' && item.content_details)
            .map(item => {
              const details = item.content_details;
              return {
                id: item.content_id,
                name: details.name,
                title: details.name,
                description: details.description,
                location: details.location,
                startDate: details.start_date,
                endDate: details.end_date,
                start_date: details.start_date,
                end_date: details.end_date,
                coverImage: details.cover_photo,
                cover_photo: details.cover_photo,
                status: details.status,
                trip_type: details.trip_type,
                tripCardPhotos: details.tripCardPhotos || null,
                savedAt: item.added_at,
                sourceType: 'board',
                boardId: board.id,
                boardName: board.name,
                tags: [],
                source: 'board',
                // Include full trip data for the card
                data: {
                  id: item.content_id,
                  name: details.name,
                  description: details.description,
                  location: details.location,
                  startDate: details.start_date,
                  endDate: details.end_date,
                  start_date: details.start_date,
                  end_date: details.end_date,
                  coverImage: details.cover_photo,
                  cover_photo: details.cover_photo,
                  status: details.status,
                  trip_type: details.trip_type,
                  tripCardPhotos: details.tripCardPhotos || null
                }
              };
            });

          boardTrips.push(...trips);
        } catch (error) {
          console.warn(`Failed to get items for board ${board.id}:`, error);
        }
      }

      // Deduplicate trips by ID (prefer board version as it has more data)
      const tripMap = new Map();

      // Add collection trips first
      collectionTrips.forEach(trip => {
        tripMap.set(trip.id, trip);
      });

      // Override with board trips (they have more complete data)
      boardTrips.forEach(trip => {
        tripMap.set(trip.id, trip);
      });

      const allTrips = Array.from(tripMap.values());

      return allTrips;
    } catch (error) {
      console.error('Error getting saved trips:', error);
      return [];
    }
  }

  // === CHECK METHODS ===

  // Check if activity is saved.
  // Returns { success, isSaved } — the shape UnifiedSaveModal reads. This
  // returned a bare boolean from Dec 2025 to Aug 2026, so the modal's
  // `savedResult.success && savedResult.isSaved` was always undefined: the
  // "Save to My Activities" toggle never showed checked and the modal's
  // unsave branch was unreachable.
  static async isActivitySaved(contentId) {
    try {
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/saves/check/${contentId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return { success: true, isSaved: data.saved === true };
    } catch (error) {
      console.error('Error checking if activity is saved:', error);
      return { success: false, isSaved: false };
    }
  }

  // Check if trip is saved (optimized - single API call)
  static async isTripSaved(tripId) {
    try {
      const response = await fetch(`${this.API_URL}/saves/trips/check/${tripId}`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await AuthService.handleResponse(response);
      return data.saved || false;
    } catch (error) {
      console.error('Error checking if trip is saved:', error);
      return false;
    }
  }

  // Lookup a saved activity's database ID by content_id (optimized helper)
  static async lookupSaveId(contentId) {
    try {
      const response = await fetch(`${this.API_URL}/saves/lookup/${contentId}`, {
        headers: AuthService.getJsonHeaders()
      });

      if (response.status === 404) {
        return null;
      }

      const data = await AuthService.handleResponse(response);
      return data.success ? data.databaseId : null;
    } catch (error) {
      console.error('Error looking up save ID:', error);
      return null;
    }
  }

  // === REMOVE METHODS ===

  // Remove saved activity (optimized - uses lookup endpoint instead of fetching all saves)
  static async unsaveActivity(activityId) {
    try {
      // Use the efficient lookup endpoint to get the database ID
      const databaseId = await this.lookupSaveId(activityId);

      if (!databaseId) {
        return { success: false, error: 'Activity not found in saves' };
      }

      // Delete using the database ID
      const deleteResponse = await fetch(`${this.API_URL}/saves/${databaseId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(deleteResponse);
    } catch (error) {
      console.error('Error unsaving activity:', error);
      return { success: false, error: error.message };
    }
  }

  // Remove a saved trip: take it off every board the user owns. A saved trip
  // is a travel_board_items row — what /saves/summary lists on the Trips tab —
  // so this is the album remove's call with the trip's type. It used to look
  // for a collection carrying metadata.originalTripId, which no collection
  // has, and always failed with "Saved trip not found" (BH-21-01).
  static async unsaveTrip(tripId) {
    try {
      const response = await fetch(
        `${SavedItemsServiceDB.API_URL}/travel-boards/items/trip/${encodeURIComponent(tripId)}`,
        { method: 'DELETE', headers: AuthService.getJsonHeaders() }
      );
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unsaving trip:', error);
      return { success: false, error: error.message };
    }
  }

  // === COLLECTION METHODS ===
  // Note: Collection management (add/remove/move) is handled via CollectionsService
  // which uses the /api/collections endpoints and the unified saved_items table

  // Get all collections/tags
  static async getAllTags() {
    try {
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/saves/collections`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.collections || [];
    } catch (error) {
      console.error('Error getting collections:', error);
      return [];
    }
  }

  // === STATS METHODS ===

  // Get saved items statistics
  static async getSavedItemsStats() {
    try {
      const response = await fetch(`${SavedItemsServiceDB.API_URL}/saves/stats`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats || {
        totalActivities: 0,
        totalTrips: 0,
        totalCollections: 0,
        mostSavedCategory: 'None',
        recentActivity: 'None'
      };
    } catch (error) {
      console.error('Error getting saved items stats:', error);
      return {
        totalActivities: 0,
        totalTrips: 0,
        totalCollections: 0,
        mostSavedCategory: 'None',
        recentActivity: 'None'
      };
    }
  }

  // === HELPER METHODS ===

  // Get saved activity by ID - uses dedicated endpoint for efficiency
  static async getSavedActivity(activityId) {
    try {
      const response = await fetch(`${this.API_URL}/saves/activity/${encodeURIComponent(activityId)}`, {
        headers: AuthService.getJsonHeaders()
      });

      if (!response.ok) {
        // Fallback to null for 404 (not found)
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await AuthService.handleResponse(response);
      return data.activity || null;
    } catch (error) {
      console.error('Error getting saved activity:', error);
      return null;
    }
  }

  // Saved activities are not archivable. archiveActivity, unarchiveActivity
  // and getArchivedActivities lived here and their endpoints are gone —
  // unsaveActivity is the one way to remove a save. getAllArchivedItems below
  // reads the archive, which now holds only collections and boards.

  /**
   * Get consolidated summary of all saved items data in a single API call
   * Replaces 9+ individual API calls for better performance
   * Returns: activities (paginated), trips, collections, boards, boardItemIds, albums, tags
   */
  static async getSummary(limit = 20, offset = 0) {
    try {
      const response = await fetch(
        `${SavedItemsServiceDB.API_URL}/saves/summary?limit=${limit}&offset=${offset}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);

      if (!data.success) {
        throw new Error(data.error || 'Failed to get saves summary');
      }

      // Transform activities to frontend format (same as getSavedActivities)
      const transformedActivities = (data.activities?.items || []).map(save => {
        const title = save.title || save.displayName || save.customTitle || save.contentId;
        const category = save.category || this.getActivityCategory(save.tags || []);
        const location = save.placeAddress || save.location || '';

        return {
          id: save.id,
          databaseId: save.databaseId,
          // The server resolves place_id through the entity spine (a saved
          // landmark with an approved Google link gets the real ChIJ… id) —
          // don't clobber it back to the content id, or the detail link and
          // place-keyed consumers lose the resolution.
          place_id: save.place_id || save.contentId,
          content_id: save.contentId,
          name: title,
          title: title,
          description: save.description || '',
          location: location,
          place_address: save.placeAddress || '',
          venue: save.venue,
          city: save.city,
          state: save.state,
          country: save.country,
          rating: save.rating,
          place_rating: save.rating,
          price: save.price,
          imageUrl: save.imageUrl,
          // Exact Commons credit (licence condition) — without it the card
          // falls back to the generic host-derived "Photo: Wikimedia Commons".
          photoAttribution: save.photoAttribution || null,
          eventDate: save.eventDate,
          eventTime: save.eventTime,
          url: save.url,
          source: save.sourceType || 'unknown',
          category: category,
          genre: save.genre,
          segment: save.segment,
          subGenre: save.subGenre,
          tags: save.tags || [],
          savedAt: save.savedAt,
          collectionId: save.collectionId,
          collectionName: save.collectionName,
          collectionColor: save.collectionColor,
          isEvent: save.isEvent,
          fromTrip: save.fromTrip || null,
          data: {
            id: save.id,
            databaseId: save.databaseId,
            place_id: save.place_id || save.contentId,
            placeId: save.place_id || save.contentId,
            name: title,
            title: title,
            description: save.description || '',
            location: location,
            place_address: save.placeAddress || '',
            venue: save.venue,
            city: save.city,
            state: save.state,
            country: save.country,
            rating: save.rating,
            price: save.price,
            imageUrl: save.imageUrl,
            photoAttribution: save.photoAttribution || null,
            eventDate: save.eventDate,
            eventTime: save.eventTime,
            url: save.url,
            source: save.sourceType || 'unknown',
            category: category,
            genre: save.genre,
            segment: save.segment,
            subGenre: save.subGenre,
            tags: save.tags || []
          }
        };
      });

      // Transform trips to frontend format
      const transformedTrips = (data.trips || []).map(trip => ({
        id: trip.trip_id,
        tripId: trip.trip_id,
        savedAt: trip.saved_at,
        savedActivityCount: trip.saved_activity_count || 0,
        tags: trip.tags ? JSON.parse(trip.tags) : [],
        data: {
          id: trip.trip_id,
          name: trip.trip_name,
          description: trip.trip_description,
          startDate: trip.start_date,
          endDate: trip.end_date,
          status: trip.trip_status,
          coverImage: trip.cover_image,
          location: trip.destination_name ? `${trip.destination_name}, ${trip.destination_country || ''}` : null
        }
      }));

      // Transform collections - add activityIds parsing, activityCount, and previewActivities
      const transformedCollections = (data.collections || []).map(c => ({
        ...c,
        activityIds: c.activityIds || [],
        activityCount: c.activity_count || c.activityCount || 0,
        previewActivities: c.previewActivities || [],
        is_owned: c.is_owned !== false,
        creator_name: c.creator_name || null,
        creator_avatar: c.creator_avatar || null
      }));

      // Transform boards - include previewDetails for AllView.
      // An absent preview stays absent (C375(2)): the server sends none when
      // its bulk read failed (C358(4)), and an empty array is an answer
      // ("nothing to show", C347). BoardCardWrapper asks for an absent one
      // itself; mapping it to [] here would show the board as empty instead.
      const transformedBoards = (data.boards || []).map(board => ({
        id: board.id,
        name: board.name,
        description: board.description,
        visibility: board.visibility,
        user_id: board.user_id,
        is_owned: board.is_owned !== false,
        created_at: board.created_at,
        updated_at: board.updated_at,
        owner_name: board.owner_name,
        item_count: board.item_count,
        previewDetails: Array.isArray(board.previewDetails) ? board.previewDetails : undefined,
        preview_items: Array.isArray(board.preview_items) ? board.preview_items
          : (Array.isArray(board.previewDetails) ? board.previewDetails : undefined),
        share_code: board.share_code
      }));

      // Transform albums. This is a whitelist — a field the server starts
      // returning stays invisible to the card until it is named here.
      const transformedAlbums = (data.albums || []).map(album => ({
        id: album.id,
        trip_id: album.trip_id,
        album_name: album.album_name,
        album_description: album.album_description,
        cover_photo: album.cover_photo,
        visibility: album.visibility,
        created_at: album.created_at,
        updated_at: album.updated_at,
        trip_name: album.trip_name,
        actual_photo_count: album.actual_photo_count,
        photo_count: album.photo_count,
        place_count: album.place_count,
        // Location cascade + recap day span read by normalizeAlbumData
        trip_location: album.trip_location,
        display_city: album.display_city,
        display_region: album.display_region,
        trip_start_date: album.trip_start_date,
        trip_end_date: album.trip_end_date,
        trip_day_ids: album.trip_day_ids,
        day_indexes: album.day_indexes,
        // These albums are saves, so most belong to somebody else: carry the
        // creator through for attribution, and is_owned to gate card actions.
        creator_id: album.creator_id,
        creator_name: album.creator_name,
        creator_avatar: album.creator_avatar,
        is_owned: album.is_owned,
        is_saved_from_board: album.is_saved_from_board,
        // Aliases for normalizeCardData compatibility
        name: album.name || album.album_name,
        title: album.title || album.album_name,
        description: album.description || album.album_description
      }));

      // Transform walking tours
      // A locked stub (C66) stays a stub: only its id, the flag and saved_at.
      const transformedWalkingTours = (data.walkingTours || []).map(tour => tour.unavailable ? tour : ({
        id: tour.id,
        user_id: tour.user_id,
        title: tour.title,
        description: tour.description,
        location: tour.location,
        visibility: tour.visibility,
        created_at: tour.created_at,
        shared_at: tour.shared_at,
        saved_at: tour.saved_at,
        save_notes: tour.save_notes,
        total_stops: tour.total_stops,
        total_distance_meters: tour.total_distance_meters,
        estimated_walking_minutes: tour.estimated_walking_minutes,
        difficulty_level: tour.difficulty_level,
        is_loop: tour.is_loop,
        is_owned: tour.is_owned || false,
        creator_name: tour.creator_name,
        creator_avatar: tour.creator_avatar,
        creator_picture: tour.creator_picture,
        preview_stops: tour.preview_stops || []
      }));

      // Transform tourism experiences
      const transformedTourismExperiences = (data.tourismExperiences || []).map(exp => exp.unavailable ? exp : ({
        id: exp.id,
        user_id: exp.user_id,
        title: exp.title,
        description: exp.description,
        location: exp.location,
        visibility: exp.visibility,
        created_at: exp.created_at,
        shared_at: exp.shared_at,
        saved_at: exp.saved_at,
        total_stops: exp.total_stops,
        total_distance_meters: exp.total_distance_meters,
        transport_mode: exp.transport_mode,
        route_type: exp.route_type,
        typical_duration_minutes: exp.typical_duration_minutes,
        experience_category: exp.experience_category,
        difficulty_level: exp.difficulty_level,
        operator_name: exp.operator_name,
        price_range_min: exp.price_range_min,
        price_range_max: exp.price_range_max,
        price_currency: exp.price_currency,
        year_round: exp.year_round,
        operating_season_start: exp.operating_season_start,
        operating_season_end: exp.operating_season_end,
        is_active: exp.is_active,
        is_owned: exp.is_owned || false,
        creator_name: exp.creator_name,
        creator_avatar: exp.creator_avatar,
        creator_picture: exp.creator_picture,
        preview_stops: exp.preview_stops || []
      }));

      return {
        success: true,
        activities: {
          items: transformedActivities,
          pagination: data.activities?.pagination
        },
        trips: transformedTrips,
        collections: transformedCollections,
        activityTaxonomy: data.activityTaxonomy || [],
        tags: data.tags || [],
        boardItemIds: data.boardItemIds || { activity: [], trip: [], album: [], collection: [] },
        boards: transformedBoards,
        albums: transformedAlbums,
        walkingTours: transformedWalkingTours,
        tourismExperiences: transformedTourismExperiences,
        archivedCount: data.archivedCount || 0
      };
    } catch (error) {
      console.error('Error getting saves summary:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all archived items across all types (activities, collections, boards, albums)
   * Single consolidated endpoint replaces 4 separate API calls.
   * @returns {Object} Object with arrays for each item type
   */
  static async getAllArchivedItems() {
    try {
      const headers = AuthService.getJsonHeaders();
      const response = await fetch(`${this.API_URL}/saves/archived-all`, { headers });
      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          activities: data.activities || [],
          collections: data.collections || [],
          boards: data.boards || [],
          albums: data.albums || []
        };
      }

      return { success: false, error: data.error, activities: [], collections: [], boards: [], albums: [] };
    } catch (error) {
      console.error('Error getting all archived items:', error);
      return { success: false, error: error.message, activities: [], collections: [], boards: [], albums: [] };
    }
  }
}

export default SavedItemsServiceDB;